import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loginUser } from "../../services/auth";
import { dispatchLoginEvent } from "../../utils/auth-events";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mountedRef = useRef(true);

  const getRedirectDestination = (userRole) => {
    const fromState = location.state?.from;
    const storedRedirect = localStorage.getItem("postLoginRedirect");
    const fromPath = fromState
      ? `${fromState.pathname || ""}${fromState.search || ""}${fromState.hash || ""}`
      : null;
    const target = fromPath || storedRedirect;
    const defaultPath = userRole === "admin" ? "/admin/dashboard" : "/dashboard";
    const destination = target && target !== "/login" ? target : defaultPath;

    if (storedRedirect || fromState) {
      localStorage.removeItem("postLoginRedirect");
    }

    return destination;
  };

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError("");

    try {
      const response = await loginUser({ email, password });
      
      localStorage.setItem("token", response.token);

      // Dispatch custom event to notify Header component
      dispatchLoginEvent();

      // Decode JWT token to get user role
      const payload = JSON.parse(atob(response.token.split(".")[1]));
      const userRole = payload.role;

      // Redirect to the original page (if any) or role-based default
      navigate(getRedirectDestination(userRole), { replace: true });
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!mountedRef.current) {
        return;
      }
      
      setError("Invalid email or password");
    } finally {
      // Check if component is still mounted before updating state
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div className="card card-conference shadow">
            <div className="card-header bg-conference-gradient text-white text-center">
              <h2 className="mb-0 text-white">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Welcome Back - Sign In
              </h2>
            </div>
            <div className="card-body p-4">
              {/* To be deleted */}
              {/* <div className="alert alert-info d-flex align-items-start" role="status">
                <i className="bi bi-info-circle me-2 mt-1"></i>
                <div>
                  <div className="fw-semibold">Important - New Submission System</div>
                  <div className="small mb-0">
                    We are now using a new conference website. All authors must create a new account before submitting a paper, even if they had an account on the previous system.
                  </div>
                </div>
              </div> */}

              {error && (
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                >
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError("")}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="form-conference">
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    <i className="bi bi-envelope me-1"></i>
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="password" className="form-label">
                    <i className="bi bi-lock me-1"></i>
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="btn btn-link p-0 small text-decoration-none text-conference-primary"
                      style={{ fontWeight: 500 }}
                    >
                      <i className="bi bi-question-circle me-1"></i>
                      Forgot password?
                    </button>
                  </div>
                </div>

                <div className="d-grid">
                  <button
                    type="submit"
                    className="btn btn-conference-primary btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-box-arrow-in-right me-2"></i>
                        Sign In
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="text-center mt-4">
                <p className="text-muted mb-0">
                  Don't have an account?
                  <a
                    href="/register"
                    className="text-decoration-none ms-1 text-conference-primary"
                  >
                    Create one
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
